import { clientLogger } from '@/lib/clientLogger';
import { useState, useRef, useEffect, useMemo, useCallback, useContext, lazy, Suspense } from 'react';
import { SplitScreenAreaContext, useSplitScreenBounds } from '@/lib/splitScreen';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { JobDTO, JobMergePatchDTO, JobTypeBasicDTO, Technician, JobStatusDTO, JobItemOperationDTO, CompanyInvoiceDTO, TagDTO, ContactDTO, InvoiceDto, ConvertJobToEstimateResultDto } from '@/types';
import CreateEstimateFromJobDialog from './CreateEstimateFromJobDialog';
import LinkedEstimates from './LinkedEstimates';
import { Calendar, Clock, User, FileText, Settings, Receipt, Camera, Trash2, ArrowRight, ArrowLeft, MapPin, DollarSign, ChevronDown, ChevronUp, Repeat, CalendarPlus, Link, Paperclip, Phone, MessageSquare, X, Zap } from 'lucide-react';

import { EditableField } from './ui/EditableField';
import { EditableAddress } from './ui/EditableAddress';
import { useExtensibleFields, getFieldValue, mergeExtraFields } from '@/hooks/useExtensibleFields';
import { calculateTotal } from '@/lib/invoiceCalculations';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { MapWidget } from './MapWidget';
import { JobItemsSection } from './ui/JobItemsSection';
import { JobContactsSection } from './contacts/JobContactsSection';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNotifications, notificationService } from './ui/NotificationSystem';

import { JobAttachmentsModal } from './JobAttachmentsModal';
import { JobExpenseAttachmentsModal } from './JobExpenseAttachmentsModal';
import { PaymentCard } from './PaymentCard';
import { JobExpensesCard } from './JobExpensesCard';
import { TechnicianPaySection } from './TechnicianPaySection';
import { TakePaymentButton } from './ui/TakePaymentButton';
import { InvoiceDetailsModal } from './InvoiceDetailsModal';
import { SmsTemplateMenu } from './SmsTemplateMenu';
import { SendSmsModal } from './SendSmsModal';
import { JobDetailsModal } from './JobDetailsModal';
import { formatDateOnlyInCompanyTimeZone, formatInstantInCompanyTimeZone, parseBackendInstantToUtcDate, isScheduleRange } from '@/lib/dateUtils';
import { TagSelector } from './ui/TagSelector';
import { JobContactConversationModal } from '@/components/JobContactConversationModal';
import { CallLauncher, type CallRequest } from './CallLauncher';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import { useCompletedJobEditLock } from '@/hooks/useCompletedJobEditLock';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePermissions } from '@/hooks/usePermissions';
import { NewJobModal } from './NewJobModal';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { useModalLayer, type ModalLayer } from '@/lib/layerManager';
import { JobContactInfoModal } from './contacts/JobContactInfoModal';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { JobVisitsModal } from './JobVisitsModal';
import { SubscriptionBanner } from './SubscriptionBanner';
import { buildTechnicianSelectOptions, formatTechnicianOptionLabel, hasInactiveTechnicians } from '@/lib/technicianSelectors';
import { AsapBadge } from '@/components/jobs/AsapBadge';
import { CallMaskingOptions } from './CallMaskingOptions';

interface JobDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Programmatic close without browser-back (see Calls/Messages navigation). */
    onDismiss?: () => void;
    job: JobDTO | null;
    onDelete?: (job: JobDTO) => void;
    onCancelJob?: (job: JobDTO, reason?: string) => void;
    onUncancelJob?: (job: JobDTO) => void;
    onJobUpdated?: (updatedJob: JobDTO) => void;
    openedFromInvoice?: boolean;  // Opened from InvoiceDetailsModal — Invoice button closes instead of re-opening
    openedFromPastJobs?: boolean;
    layer?: ModalLayer;
}

const VoipCallHistoryPage = lazy(() => import('./VoipCallHistoryPage').then(m => ({ default: m.VoipCallHistoryPage })));

export function JobDetailsDesktop({ isOpen, onClose, onDismiss, job, onDelete, onCancelJob, onUncancelJob, onJobUpdated, openedFromInvoice, layer }: JobDetailsModalProps) {
    const isMobile = useIsMobile();

    // Split screen open: cover only the side this job was opened from, tracking that side's size.
    const splitArea = useContext(SplitScreenAreaContext);
    const splitBounds = useSplitScreenBounds();
    const [showCallsModal, setShowCallsModal] = useState(false);
    const callsLayer = useModalLayer({
        enabled: showCallsModal,
        onClose: () => setShowCallsModal(false),
    });
    // "Contact Info" modal: the id of the contact whose tab is showing, null when closed.
    const [contactInfoId, setContactInfoId] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { openEstimate } = useVoiceCall();
    const { canEditJobs, canEditField: canEditFieldRaw, canEditCompletedJobs } = useJobEditPermissions();
    const { userRole, isAdmin } = useCurrentUser();
    const { isFeatureAllowed } = usePermissions();
    const isTechnician = userRole === 'technician';

    // State definitions
    const [showImagesModal, setShowImagesModal] = useState(false);
    const [showExpenseAttachmentsModal, setShowExpenseAttachmentsModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [showConfirmUncancel, setShowConfirmUncancel] = useState(false);
    const [showConfirmMarkAsap, setShowConfirmMarkAsap] = useState(false);
    const [isJobInfoSidebarExpanded, setIsJobInfoSidebarExpanded] = useState(false);
    const [scheduleExpanded, setScheduleExpanded] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [conversationModalState, setConversationModalState] = useState<{
        isOpen: boolean;
        contactId: string;
        which: 'primary' | 'additional';
        masked: boolean;
        contactName: string;
        jobId: string;
    }>({ isOpen: false, contactId: '', which: 'primary', masked: false, contactName: '', jobId: '' });
    // "SMS" opens an anchored SMS template list; picking one prefills a locked
    // SendSmsModal, "Custom message" opens the conversation modal as the contact row already does.
    const [showSmsMenu, setShowSmsMenu] = useState(false);
    const smsMenuRef = useRef<HTMLDivElement>(null);
    const smsMenuLayer = useModalLayer({
        enabled: showSmsMenu,
        onClose: () => setShowSmsMenu(false),
    });
    const [templateSmsState, setTemplateSmsState] = useState<{
        message: string;
        warning?: string;
        recipientPhone: string;
        recipientName: string;
        toContactRef?: string;
    } | null>(null);
    // With several contacts, the menu first asks which one to text; a single contact skips that step.
    const jobContacts = (job?.contacts ?? []).map((c) => c.contact).filter((c) => !!c?.id);
    const smsContacts = jobContacts.filter((c) => !!c.phone);
    const [smsContactId, setSmsContactId] = useState<string | null>(null);
    const smsContact = smsContacts.length === 1 ? smsContacts[0] : smsContacts.find((c) => c.id === smsContactId);

    useEffect(() => {
        if (!showSmsMenu) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (smsMenuRef.current && !smsMenuRef.current.contains(event.target as Node)) setShowSmsMenu(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showSmsMenu]);

    const openClientConversation = (contact: (typeof smsContacts)[number]) => {
        if (!contact.phone || !job) return;
        setConversationModalState({
            isOpen: true,
            contactId: contact.id,
            which: 'primary',
            masked: contact.phone_masked ?? false,
            contactName: contact.name || 'Client',
            jobId: job.id,
        });
    };

    const [callRequest, setCallRequest] = useState<CallRequest | null>(null);
    const [pastJobId, setPastJobId] = useState<string | null>(null);
    const [showConvertConfirm, setShowConvertConfirm] = useState(false);
    const [isScheduleVisitOpen, setIsScheduleVisitOpen] = useState(false);
    const [viewOriginJobId, setViewOriginJobId] = useState<string | null>(null);
    const [viewFollowUpJobId, setViewFollowUpJobId] = useState<string | null>(null);
    const [showItemEditSignatureWarning, setShowItemEditSignatureWarning] = useState(false);
    const [pendingItemOperations, setPendingItemOperations] = useState<JobItemOperationDTO[] | null>(null);
    const pendingItemPromiseRef = useRef<{ resolve: () => void; reject: (e: Error) => void } | null>(null);
    const statusDropdownRef = useRef<HTMLDivElement>(null);
    const jobDetailsWasOpenRef = useRef(false);
    const selectedCompanyIdWhenJobDetailsOpenedRef = useRef<string>('');
    const [showVisitsModal, setShowVisitsModal] = useState(false);
    const [showInactiveTechnicians, setShowInactiveTechnicians] = useState(false);

    // Click outside handler for status dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setShowStatusDropdown(false);
            }
        };

        if (showStatusDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showStatusDropdown]);

    const getStatusStyle = (job: JobDTO) => {
        const backgroundColor = job.status_color || '#64748b'; // Default to secondary-500
        // Calculate text color based on background brightness for contrast
        const hex = backgroundColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        const textColor = brightness > 128 ? '#0f172a' : '#ffffff'; // secondary-900 or white

        return {
            backgroundColor,
            color: textColor,
            border: 'none',
        };
    };

    // Extensible Fields
    const { fieldConfigs } = useExtensibleFields('jobs', job?.company?.id);
    const jobExtensibleFields = fieldConfigs || [];

    const mergedExtraFields = mergeExtraFields(job?.extra_fields, jobExtensibleFields);

    // Fetch job types, technicians, job sources, and job statuses
    const { data: jobTypes, isLoading: jobTypesLoading } = useQuery({
        queryKey: ['jobTypes', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getJobTypesByCompany() : Promise.resolve([]),
        enabled: isOpen && !!job?.company?.id,
    });

    const { data: technicians, isLoading: techniciansLoading } = useQuery({
        queryKey: ['technicians', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getTechnicians(job.company.id) : Promise.resolve([]),
        enabled: isOpen && !!job?.company?.id,
    });

    useEffect(() => {
        setShowInactiveTechnicians(false);
    }, [job?.company?.id]);

    const technicianOptions = useMemo(() => buildTechnicianSelectOptions(
        technicians,
        showInactiveTechnicians,
        job?.assigned_technician?.id || '',
        { value: '', label: 'Unassigned' },
    ), [technicians, showInactiveTechnicians, job?.assigned_technician?.id]);

    const { data: jobSources, isLoading: jobSourcesLoading } = useQuery({
        queryKey: ['jobSources', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getJobSourcesByCompany() : Promise.resolve([]),
        enabled: isOpen && !!job?.company?.id,
    });

    const { data: jobStatuses, isLoading: jobStatusesLoading } = useQuery({
        queryKey: ['jobStatuses', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getJobStatusesByCompany(job.company.id) : Promise.resolve([]),
        enabled: isOpen && !!job?.company?.id,
    });

    const { data: jobAttachmentsList } = useQuery({
        queryKey: ['jobAttachments', job?.id],
        queryFn: () => apiService.getJobAttachments(job!.id),
        enabled: isOpen && !!job?.id,
    });
    const attachmentCount = jobAttachmentsList?.length ?? 0;

    const { data: jobExpenseAttachmentsList } = useQuery({
        queryKey: ['jobExpenseAttachments', job?.id],
        queryFn: () => apiService.getJobExpenseAttachments(job!.id),
        enabled: isOpen && !!job?.id,
    });
    const expenseAttachmentCount = jobExpenseAttachmentsList?.length ?? 0;

    const { isCompletedJobLocked } = useCompletedJobEditLock(job, canEditCompletedJobs, isOpen);

    // Wrap canEditField to block editing on completed jobs
    const canEditField = useCallback((field: string): boolean => {
        if (isCompletedJobLocked) return false;
        return canEditFieldRaw(field);
    }, [isCompletedJobLocked, canEditFieldRaw]);

    // Fetch company invoice settings to get tax rate
    const { data: companyInvoiceSettings } = useQuery<CompanyInvoiceDTO | null>({
        queryKey: ['company-invoice-settings', job?.company?.id],
        // Pass the job's company: with no argument the interceptor uses the globally selected
        // company, caching another company's tax rate under this job's key.
        queryFn: () => job?.company?.id ? apiService.getCompanyInvoiceSettings(job.company.id) : Promise.resolve(null),
        enabled: isOpen && !!job?.company?.id,
    });
    const { companyTimezone, selectedCompanyId } = useGlobalCompany();
    const companyTimeZone = companyTimezone?.trim();

    // Close when the header company selector switches to a specific company (not "All Companies")
    // while this modal is open, so the user is not left viewing a job under the wrong tenant filter.
    useEffect(() => {
        if (!isOpen) {
            jobDetailsWasOpenRef.current = false;
            return;
        }
        if (!jobDetailsWasOpenRef.current) {
            selectedCompanyIdWhenJobDetailsOpenedRef.current = selectedCompanyId;
            jobDetailsWasOpenRef.current = true;
            return;
        }
        if (selectedCompanyId && selectedCompanyId !== selectedCompanyIdWhenJobDetailsOpenedRef.current) {
            onClose();
        }
    }, [isOpen, selectedCompanyId, onClose]);

    // Update job mutation - uses merge-patch to only send changed fields
    const updateJobMutation = useMutation({
        mutationFn: (data: JobMergePatchDTO) => apiService.mergePatchJob(job!.id, data),
        onSuccess: (updatedJob: JobDTO) => {
            queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
                if (!existing) return existing;
                const isArray = Array.isArray(existing);
                const items = isArray ? existing : existing.jobs;
                if (!Array.isArray(items)) return existing;
                const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
                if (isArray) return updatedItems;
                return { ...existing, jobs: updatedItems };
            });
            queryClient.setQueryData(['job', updatedJob.id], updatedJob);
            if (onJobUpdated) {
                onJobUpdated(updatedJob);
            }
        },
        onError: (error: any) => {
            clientLogger.error('Error updating job:', error);
        },
    });

    // Update job tags mutation
    const setJobTagsMutation = useMutation({
        mutationFn: async (tags: TagDTO[]) => {
            if (!job) return;
            const tagInputs = tags.map(tag => ({ tag_id: tag.id }));
            return apiService.setJobTags(job.id, tagInputs);
        },
        onSuccess: (updatedTags) => {
            // Update the job in the cache with new tags
            const updatedJob = { ...job!, tags: updatedTags };
            queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
                if (!existing) return existing;
                const isArray = Array.isArray(existing);
                const items = isArray ? existing : existing.jobs;
                if (!Array.isArray(items)) return existing;
                const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
                if (isArray) return updatedItems;
                return { ...existing, jobs: updatedItems };
            });
            queryClient.setQueryData(['job', updatedJob.id], updatedJob);
            if (onJobUpdated) {
                onJobUpdated(updatedJob);
            }
        },
        onError: (error: any) => {
            clientLogger.error('Error updating job tags:', error);
        },
    });

    const handleFieldSave = async (field: keyof JobMergePatchDTO, value: any) => {
        if (!job) return;

        try {
            // Convert empty strings to null for nullable fields
            const cleanValue = value === '' ? null : value;

            await updateJobMutation.mutateAsync({
                [field]: cleanValue
            });
        } catch (error) {
            clientLogger.error(`Failed to update ${field}:`, error);
            throw error;
        }
    };

    /** Collapsed schedule: set both start and end to the same instant. */
    const handleScheduleSingleSave = async (val: any) => {
        if (!job) return;
        const cleanValue = val === '' ? null : val;
        try {
            await updateJobMutation.mutateAsync({
                date_executed_start: cleanValue,
                date_executed_end: cleanValue,
                // Saving the placeholder time unchanged must still clear ASAP.
                ...(job.is_asap ? { is_asap: false } : {}),
            });
        } catch (error) {
            clientLogger.error('Failed to update schedule:', error);
            throw error;
        }
    };

    /** When start moves past end, align end to start (same as JobDetailsMobile schedule pickers). */
    const handleScheduleStartSave = async (val: any) => {
        if (!job) return;
        const cleanValue = val === '' ? null : val;
        try {
            const startMs = cleanValue ? parseBackendInstantToUtcDate(String(cleanValue)).getTime() : NaN;
            const endMs = job.date_executed_end ? parseBackendInstantToUtcDate(job.date_executed_end).getTime() : NaN;
            const startAfterEnd =
                cleanValue &&
                job.date_executed_end &&
                !Number.isNaN(startMs) &&
                !Number.isNaN(endMs) &&
                startMs > endMs;

            if (startAfterEnd) {
                await updateJobMutation.mutateAsync({
                    date_executed_start: cleanValue,
                    date_executed_end: cleanValue,
                });
            } else {
                await updateJobMutation.mutateAsync({
                    date_executed_start: cleanValue,
                });
            }
        } catch (error) {
            clientLogger.error('Failed to update date_executed_start:', error);
            throw error;
        }
    };

    const handleExtraFieldSave = async (key: string, value: any) => {
        if (!job) return;
        const newExtraFields = { ...(job.extra_fields || {}), [key]: value };
        await handleFieldSave('extra_fields', newExtraFields);
    };

    // Fetch the invoice for this job (to check for signature status)
    const { data: jobInvoice } = useQuery<InvoiceDto | null>({
        queryKey: ['invoice', job?.id],
        queryFn: () => job?.id ? apiService.getInvoiceByJobId(job.id).catch(() => null) : Promise.resolve(null),
        enabled: isOpen && !!job?.id,
    });

    const handleJobItemsChange = async (operations: JobItemOperationDTO[]) => {
        if (!job) return;

        // Check if the job has a signed (non-voided) invoice
        if (jobInvoice?.signature && !jobInvoice?.is_signature_voided) {
            const wouldChangeAmount = operations.some(op => {
                if (op.operation === 'Add' || op.operation === 'Remove') return true;
                if (op.operation === 'Edit' && op.item.id) {
                    const existing = (job.items || []).find((i: any) => i.id === op.item.id);
                    if (!existing) return true;
                    return (
                        existing.price !== op.item.price ||
                        existing.quantity !== op.item.quantity ||
                        existing.discount !== op.item.discount ||
                        existing.taxable !== op.item.taxable
                    );
                }
                return false;
            });

            if (wouldChangeAmount) {
                return new Promise<void>((resolve, reject) => {
                    setPendingItemOperations(operations);
                    pendingItemPromiseRef.current = { resolve, reject };
                    setShowItemEditSignatureWarning(true);
                });
            }
        }

        await executeItemOperations(operations);
    };

    const executeItemOperations = async (operations: JobItemOperationDTO[]) => {
        if (!job) return;

        // Apply operations to current items to compute new items list
        let newItems = [...(job.items || [])];
        for (const op of operations) {
            if (op.operation === 'Add') {
                newItems.push(op.item);
            } else if (op.operation === 'Remove' && op.item.id) {
                newItems = newItems.filter(item => (item as any).id !== op.item.id);
            } else if (op.operation === 'Edit' && op.item.id) {
                newItems = newItems.map(item =>
                    (item as any).id === op.item.id ? op.item : item
                );
            }
        }

        // Calculate the new total (including tax)
        const taxRate = companyInvoiceSettings?.tax_rate || 0;
        const newTotal = calculateTotal(newItems, taxRate);

        try {
            await updateJobMutation.mutateAsync({
                item_operations: operations,
                total: newTotal
            });
            // Item changes may have voided a signature server-side, which preserves the
            // previously-signed invoice as a PDF attachment. Refresh the attachments list
            // so the new attachment is visible.
            queryClient.invalidateQueries({ queryKey: ['jobAttachments', job.id] });
        } catch (error) {
            clientLogger.error('Failed to update job items:', error);
            throw error;
        }
    };

    const handleConfirmItemEditSignatureWarning = async () => {
        if (pendingItemOperations) {
            try {
                await executeItemOperations(pendingItemOperations);
                pendingItemPromiseRef.current?.resolve();
            } catch (e) {
                pendingItemPromiseRef.current?.reject(e as Error);
            } finally {
                setPendingItemOperations(null);
                pendingItemPromiseRef.current = null;
            }
        }
    };

    const handleCancelItemEditSignatureWarning = () => {
        setShowItemEditSignatureWarning(false);
        setPendingItemOperations(null);
        pendingItemPromiseRef.current?.reject(new Error('cancelled'));
        pendingItemPromiseRef.current = null;
    };

    const handleAttachmentsUpdated = () => {
        queryClient.invalidateQueries({ queryKey: ['jobAttachments', job?.id] });
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        if (onJobUpdated && job) {
            onJobUpdated({ ...job });
        }
    };

    const handleExpenseAttachmentsUpdated = () => {
        queryClient.invalidateQueries({ queryKey: ['jobExpenseAttachments', job?.id] });
    };

    // Estimate created from this job (job kept or deleted): close the job and open the estimate,
    // which links back to the job when it was kept.
    const handleEstimateCreated = (result: ConvertJobToEstimateResultDto) => {
        setShowConvertConfirm(false);
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        queryClient.invalidateQueries({ queryKey: ['estimates'] });
        queryClient.invalidateQueries({ queryKey: ['job-estimates'] });
        openLinkedEstimate(result.estimate_id);
    };

    const openLinkedEstimate = (estimateId: string) => {
        // Skip history.back() so the estimate overlay can push its own layer without a race.
        (onDismiss ?? onClose)();
        openEstimate(estimateId);
    };

    if (!job) return null;

    const paddingClass = isMobile ? 'p-4' : 'p-6';
    const hasAddress = job.address && job.address.trim().length > 0;

    return (
        // Desktop: start below the Dashboard's top bar (h-12) so the bell and clock stay visible.
        // Phones keep the full-screen view.
        <div className={`fixed top-0 bottom-0 left-0 ${(layer?.depth ?? 1) > 1 ? '' : `lg:left-64 xl:left-[var(--sidebar-width,16rem)] ${isMobile ? '' : 'lg:top-12'}`} right-0 bg-white flex flex-col animate-fade-in`} style={{ ...layer?.style, ...splitBounds }}>
            {/* This modal covers the whole page, including the Dashboard's status strip — repeat it here. */}
            {/* In split screen the job sits below the Dashboard's strip, which stays visible. */}
            {!splitArea && <div className="shrink-0"><SubscriptionBanner /></div>}
            {/* Header - Fixed at top by parent flex-col */}
            <div className={`flex items-start justify-between ${paddingClass} border-b border-gray-200 bg-white shrink-0 z-10`}>
                <div className="flex items-start gap-3">
                    <button
                        onClick={onClose}
                        className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 mt-0.5"
                        title="Back to Jobs"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="flex flex-col min-w-0 gap-1.5">
                        <div className="flex items-center gap-3">
                            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900 truncate`}>
                                Job #{job.invoice_number || 'N/A'}
                            </h2>
                            {/* Company, Status - inline display - hidden on very small screens if overflowing */}
                            <div className="hidden md:flex items-center gap-3 flex-wrap">
                                {/* Status or Canceled Badge */}
                                {job.canceled_at ? (
                                    <span
                                        className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-red-100 text-red-800"
                                        title={job.cancel_reason ? `Reason: ${job.cancel_reason}` : undefined}
                                    >
                                        Canceled {job.cancel_reason && ` (${job.cancel_reason})`}
                                    </span>
                                ) : (
                                    <div className="relative" ref={statusDropdownRef}>
                                        <button
                                            type="button"
                                            onClick={() => canEditField('status') && setShowStatusDropdown(!showStatusDropdown)}
                                            disabled={jobStatusesLoading || !canEditField('status')}
                                            className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold w-fit transition-all ${canEditField('status') ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}
                                            style={getStatusStyle(job)}
                                        >
                                            <span className={canEditField('status') ? 'mr-1' : ''}>
                                                {typeof job.status === 'string' ? job.status : ((job.status as any)?.name || 'No Status')}
                                            </span>
                                            {canEditField('status') && <ChevronDown className="h-3 w-3 opacity-70" />}
                                        </button>

                                        {/* Status Dropdown */}
                                        {showStatusDropdown && jobStatuses && jobStatuses.length > 0 && (
                                            <div
                                                className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2 flex flex-col"
                                            >
                                                {jobStatuses.map((status: JobStatusDTO) => {
                                                    const statusBgColor = status.color || '#808080';
                                                    const hex = statusBgColor.replace('#', '');
                                                    const r = parseInt(hex.substr(0, 2), 16);
                                                    const g = parseInt(hex.substr(2, 2), 16);
                                                    const b = parseInt(hex.substr(4, 2), 16);
                                                    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                                                    const textColor = brightness > 128 ? '#000000' : '#FFFFFF';

                                                    const currentStatusId = typeof job.status === 'string'
                                                        ? jobStatuses.find((s: JobStatusDTO) => s.name === job.status)?.id
                                                        : (job.status as any)?.id;

                                                    return (
                                                        <button
                                                            key={status.id}
                                                            type="button"
                                                            onClick={() => {
                                                                handleFieldSave('status_id', status.id);
                                                                setShowStatusDropdown(false);
                                                            }}
                                                            className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${status.id === currentStatusId ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                                                        >
                                                            <div
                                                                className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold mr-2"
                                                                style={{
                                                                    backgroundColor: statusBgColor,
                                                                    color: textColor
                                                                }}
                                                            >
                                                                {status.name}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-base text-gray-500 mt-1">
                            {/* Editable Schedule */}
                            <div className="flex items-center gap-1.5">
                                <Calendar className="h-5 w-5 text-amber-500" />
                                <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap">
                                    {(() => {
                                        if (job.is_asap) {
                                            // ASAP: the date is a placeholder, so show the badge and let the user set a real time.
                                            return (
                                                <div className="flex items-center gap-2">
                                                    <AsapBadge />
                                                    {canEditField('date') && (
                                                        <EditableField
                                                            value={job.date_executed_start}
                                                            type="datetime-local"
                                                            timezone={companyTimeZone}
                                                            onSave={handleScheduleSingleSave}
                                                            inline={true}
                                                            renderValue={() => (
                                                                <span className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
                                                                    Set time
                                                                </span>
                                                            )}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }
                                        const showRange = scheduleExpanded || isScheduleRange(job.date_executed_start, job.date_executed_end);
                                        if (!showRange) {
                                            // Collapsed: single date+time that sets both start and end.
                                            return (
                                                <div className="flex flex-col items-start gap-0.5">
                                                    <EditableField
                                                        value={job.date_executed_start}
                                                        type="datetime-local"
                                                        timezone={companyTimeZone}
                                                        onSave={handleScheduleSingleSave}
                                                        inline={true}
                                                        disabled={!canEditField('date')}
                                                        renderValue={(val) => {
                                                            if (!val) return <span className="text-gray-400 text-base hover:text-blue-600 transition-colors cursor-pointer">Set date...</span>;
                                                            return (
                                                                <span className="text-gray-900 font-medium text-base hover:text-blue-600 transition-colors cursor-pointer">
                                                                    {formatInstantInCompanyTimeZone(val, companyTimeZone)}
                                                                </span>
                                                            );
                                                        }}
                                                    />
                                                    {canEditField('date') && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setScheduleExpanded(true)}
                                                            className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                                                            title="Set a separate end date & time"
                                                        >
                                                            + Add end date
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        }
                                        // Expanded: separate Start / End fields.
                                        return (
                                            <>
                                                <EditableField
                                                    value={job.date_executed_start}
                                                    type="datetime-local"
                                                    timezone={companyTimeZone}
                                                    onSave={handleScheduleStartSave}
                                                    inline={true}
                                                    disabled={!canEditField('date')}
                                                    renderValue={(val) => {
                                                        if (!val) return <span className="text-gray-400 text-base hover:text-blue-600 transition-colors cursor-pointer">Set start...</span>;
                                                        return (
                                                            <span className="text-gray-900 font-medium text-base hover:text-blue-600 transition-colors cursor-pointer">
                                                                {formatInstantInCompanyTimeZone(val, companyTimeZone)}
                                                            </span>
                                                        );
                                                    }}
                                                />
                                                <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                <EditableField
                                                    value={job.date_executed_end}
                                                    type="datetime-local"
                                                    timezone={companyTimeZone}
                                                    onSave={(val) => handleFieldSave('date_executed_end', val)}
                                                    inline={true}
                                                    disabled={!canEditField('date')}
                                                    validate={(val) => {
                                                        if (!val || !job.date_executed_start) return null;
                                                        const endMs = parseBackendInstantToUtcDate(String(val)).getTime();
                                                        const startMs = parseBackendInstantToUtcDate(job.date_executed_start).getTime();
                                                        if (!Number.isNaN(endMs) && !Number.isNaN(startMs) && endMs < startMs) {
                                                            return 'End date cannot be before start date';
                                                        }
                                                        return null;
                                                    }}
                                                    renderValue={(val) => {
                                                        if (!val) return <span className="text-gray-400 text-base hover:text-blue-600 transition-colors cursor-pointer">Set end...</span>;
                                                        return (
                                                            <span className="text-gray-900 font-medium text-base hover:text-blue-600 transition-colors cursor-pointer">
                                                                {formatInstantInCompanyTimeZone(val, companyTimeZone)}
                                                            </span>
                                                        );
                                                    }}
                                                />
                                                {canEditField('date') && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setScheduleExpanded(false);
                                                            handleFieldSave('date_executed_end', job.date_executed_start);
                                                        }}
                                                        className="flex items-center gap-0.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors flex-shrink-0"
                                                        title="Use a single date & time"
                                                    >
                                                        <ChevronUp className="h-3.5 w-3.5" />
                                                        Single time
                                                    </button>
                                                )}
                                            </>
                                        );
                                    })()}
                                    {!job.is_asap && canEditField('date') && (
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmMarkAsap(true)}
                                            className="flex items-center gap-0.5 text-xs font-medium text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                                            title="Mark this job ASAP (no scheduled time)"
                                        >
                                            <Zap className="h-3.5 w-3.5" />
                                            Mark ASAP
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Editable Technician */}
                            <div className="flex items-center gap-1.5">
                                <User className="h-5 w-5 text-sky-500" />
                                <EditableField
                                    value={job.assigned_technician?.id}
                                    type="select"
                                    options={technicianOptions}
                                    onSave={(val) => handleFieldSave('assigned_technician_id', val)}
                                    placeholder="Assign Technician"
                                    disabled={techniciansLoading || !canEditField('technician')}
                                    inline={true}
                                    selectFooterAction={hasInactiveTechnicians(technicians) && !showInactiveTechnicians
                                        ? {
                                            label: 'Show inactive technicians...',
                                            onClick: () => setShowInactiveTechnicians(true),
                                        }
                                        : undefined}
                                    renderValue={(val) => {
                                        const technician = technicians?.find((e: Technician) => e.id === val) || job.assigned_technician;
                                        if (!technician) return <span className="text-gray-400 text-base hover:text-blue-600 transition-colors cursor-pointer">Unassigned</span>;
                                        return <span className="text-gray-900 font-medium text-base hover:text-blue-600 transition-colors cursor-pointer">{formatTechnicianOptionLabel(technician.name, technician.status)}</span>;
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    {onDelete && (
                        <button
                            onClick={() => setShowConfirmDelete(true)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Job"
                        >
                            <Trash2 className="h-5 w-5 text-red-500" />
                        </button>
                    )}
                </div>
            </div>

            {/* Pending Approval Banner */}
            {job.pending_approval && (
                <div className={`flex items-center justify-between ${paddingClass} py-2 bg-amber-50 border-b border-amber-200 shrink-0`}>
                    <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                        <Clock className="h-4 w-4" />
                        This job is pending admin approval
                    </div>
                    {!isTechnician && (
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    await apiService.approveJob(job.id);
                                    queryClient.invalidateQueries({ queryKey: ['jobs'] });
                                    queryClient.invalidateQueries({ queryKey: ['job', job.id] });
                                    if (onJobUpdated) {
                                        onJobUpdated({ ...job, pending_approval: false });
                                    }
                                    addNotification(notificationService.success('Job approved'));
                                } catch (error: any) {
                                    addNotification(notificationService.error('Failed to approve job'));
                                }
                            }}
                            className="px-3 py-1 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
                        >
                            Approve
                        </button>
                    )}
                </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                <div className={paddingClass}>
                    {isMobile && hasAddress && (
                        <div className="mb-4">
                            <MapWidget address={job.address} height="250px" />
                        </div>
                    )}
                    <div className={`grid grid-cols-1 ${!isMobile ? 'lg:grid-cols-[1fr_400px]' : ''} ${isMobile ? 'gap-4' : 'gap-6'}`}>
                        {/* Main Content */}
                        <div>
                            <div className={`grid grid-cols-1 ${!isMobile ? 'lg:grid-cols-[2fr_3fr]' : ''} ${isMobile ? 'gap-4' : 'gap-6'}`}>
                                {/* Left Column - Job Details Card */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-6">

                                    {/* Location */}
                                    <div className="space-y-1">
                                        <label className="flex items-center gap-1.5 text-base font-medium text-gray-600">
                                            <MapPin className="h-5 w-5 text-rose-500" />
                                            Location
                                        </label>
                                        <EditableAddress
                                            alwaysShowEdit
                                            value={job.address || ''}
                                            onSave={async (address, unitName) => {
                                                await updateJobMutation.mutateAsync({
                                                    address: address || undefined,
                                                    unit_name: unitName || undefined
                                                });
                                            }}
                                            placeholder="Enter address"
                                            showUnitField={true}
                                            unitValue={job.unit_name || ''}
                                            disabled={!canEditField('address')}
                                        />
                                    </div>

                                    {/* Contacts */}
                                    <JobContactsSection
                                        job={job}
                                        onJobUpdated={onJobUpdated}
                                        disabled={!canEditField('contacts')}
                                        onSmsClick={(contact: ContactDTO, which: 'primary' | 'additional' = 'primary') => {
                                            setConversationModalState({
                                                isOpen: true,
                                                contactId: contact.id,
                                                which,
                                                masked: contact.phone_masked ?? false,
                                                contactName: contact.name || 'Client',
                                                jobId: job.id,
                                            });
                                        }}
                                        onCallClick={(contact: ContactDTO, which: 'primary' | 'additional' = 'primary') => {
                                            const toE164 = which === 'additional' ? contact.additional_phone : contact.phone;
                                            if (!toE164) return;
                                            setCallRequest({
                                                toE164,
                                                displayName: contact.name || undefined,
                                                jobId: job.id,
                                            });
                                        }}
                                        onPastJobSelect={(jobId) => {
                                            if (jobId !== job.id) {
                                                setPastJobId(jobId);
                                            }
                                        }}
                                        onContactInfoClick={(contact) => setContactInfoId(contact.id)}
                                    />

                                    {/* Job Type */}
                                    <div className="space-y-1">
                                        <label className="flex items-center gap-1.5 text-base font-medium text-gray-600">
                                            <FileText className="h-5 w-5 text-cyan-500" />
                                            Job Type
                                        </label>
                                        <EditableField
                                            alwaysShowEdit
                                            value={job.job_type?.id}
                                            type="select"
                                            options={[
                                                { value: '', label: 'No Job Type' },
                                                ...(jobTypes?.map((jobType: JobTypeBasicDTO) => ({
                                                    value: jobType.id,
                                                    label: jobType.name
                                                })) || [])
                                            ]}
                                            onSave={(val) => handleFieldSave('job_type_id', val)}
                                            disabled={jobTypesLoading || !canEditField('job_type')}
                                            inline={true}
                                            renderValue={(val) => {
                                                const jobType = jobTypes?.find((t: JobTypeBasicDTO) => t.id === val) || job.job_type;
                                                return <span className="text-base text-gray-900">{jobType?.name || 'No Job Type'}</span>;
                                            }}
                                        />
                                    </div>

                                    {/* Description */}
                                    <div className="space-y-1">
                                        <label className="flex items-center gap-1.5 text-base font-medium text-gray-600">
                                            <FileText className="h-5 w-5 text-blue-500" />
                                            Description
                                        </label>
                                        <EditableField
                                            alwaysShowEdit
                                            value={job.description || ''}
                                            type="textarea"
                                            onSave={(val) => handleFieldSave('description', val)}
                                            placeholder="Describe the job..."
                                            disabled={!canEditField('description')}
                                            renderValue={(val) => {
                                                if (!val || String(val).trim() === '') {
                                                    return <span className="text-gray-400 italic">empty</span>;
                                                }
                                                return <span className="text-gray-900 break-words whitespace-pre-wrap">{val}</span>;
                                            }}
                                        />
                                    </div>

                                    {/* Estimate */}
                                    <div className="space-y-1">
                                        <label className="flex items-center gap-1.5 text-base font-medium text-gray-600">
                                            <DollarSign className="h-5 w-5 text-emerald-500" />
                                            Estimate
                                        </label>
                                        <EditableField
                                            alwaysShowEdit
                                            value={job.estimate}
                                            type="text"
                                            onSave={(val) => handleFieldSave('estimate', val)}
                                            renderValue={(val) => <span className="text-base text-gray-900">{val || ''}</span>}
                                            placeholder="Enter estimate"
                                            inline={true}
                                            disabled={!canEditField('estimate')}
                                        />
                                    </div>

                                    {/* Notes */}
                                    <div className="space-y-1">
                                        <label className="flex items-center gap-1.5 text-base font-medium text-gray-600">
                                            <FileText className="h-5 w-5 text-gray-500" />
                                            Notes
                                        </label>
                                        <EditableField
                                            alwaysShowEdit
                                            value={job.notes}
                                            type="textarea"
                                            onSave={(val) => handleFieldSave('notes', val)}
                                            placeholder="Add notes..."
                                            disabled={!canEditField('notes')}
                                            renderValue={(val) => {
                                                if (!val || String(val).trim() === '') {
                                                    return <span className="text-gray-400 italic">empty</span>;
                                                }
                                                return <span className="text-gray-900 break-words whitespace-pre-wrap">{val}</span>;
                                            }}
                                        />
                                    </div>

                                    {/* Internal Notes - hidden for technician role */}
                                    {!isTechnician && (
                                        <div className="space-y-1">
                                            <label className="flex items-center gap-1.5 text-base font-medium text-gray-600">
                                                <FileText className="h-5 w-5 text-amber-500" />
                                                Internal Notes
                                            </label>
                                            <EditableField
                                                alwaysShowEdit
                                                value={job.internal_notes || ''}
                                                type="textarea"
                                                onSave={(val) => handleFieldSave('internal_notes', val)}
                                                placeholder="Add internal notes (admin only)..."
                                                disabled={!canEditField('internal_notes')}
                                                renderValue={(val) => {
                                                    if (!val || String(val).trim() === '') {
                                                        return <span className="text-gray-400 italic">empty</span>;
                                                    }
                                                    return <span className="text-gray-900 break-words whitespace-pre-wrap">{val}</span>;
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* Additional Fields */ }
                                    {mergedExtraFields.length > 0 && mergedExtraFields.map((mergedField) => {
                                        if (mergedField.type !== 'Text' && mergedField.type !== 'Boolean') return null;
                                        return (
                                            <div className="space-y-1" key={mergedField.key}>
                                                <label className="flex items-center gap-1.5 text-base font-medium text-gray-600">
                                                    <Settings className="h-5 w-5 text-purple-500" />
                                                    {mergedField.key}
                                                </label>
                                                <EditableField
                                                    alwaysShowEdit
                                                    value={getFieldValue(job.extra_fields, mergedField.key)}
                                                    onSave={(val) => handleExtraFieldSave(mergedField.key, val)}
                                                    type={mergedField.type === 'Boolean' ? 'boolean' : 'text'}
                                                    placeholder={mergedField.config?.default_value}
                                                    disabled={!canEditJobs}
                                                    inline={mergedField.type === 'Boolean' ? true : false}
                                                    renderValue={(val) => {
                                                        if (mergedField.type === 'Boolean') {
                                                            return <span className="text-base text-gray-900">{val ? 'Yes' : 'No'}</span>;
                                                        }
                                                        if (!val || String(val).trim() === '') {
                                                            return <span className="text-gray-400 italic">empty</span>;
                                                        }
                                                        return <span className="text-base text-gray-900 break-words">{val}</span>;
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}

                                </div>

                                {/* Right Column */}
                                <div className="space-y-6">

                                    {/* Job Items Section */}
                                    <JobItemsSection
                                        items={job.items || []}
                                        companyId={job.company?.id}
                                        taxRate={companyInvoiceSettings?.tax_rate || 0}
                                        defaultItemTaxable={companyInvoiceSettings?.default_item_taxable ?? true}
                                        onItemsChange={handleJobItemsChange}
                                        isEditable={canEditField('total')}
                                        estimate={job.estimate}
                                        onEstimateChange={canEditField('total') ? async (estimate) => {
                                            await updateJobMutation.mutateAsync({ estimate });
                                        } : undefined}
                                    />

                                    {/* Payments */}
                                    <PaymentCard job={job} onJobUpdated={onJobUpdated} disabled={!canEditField('payments')} />

                                    {/* Expenses */}
                                    <JobExpensesCard job={job} onJobUpdated={onJobUpdated} disabled={!canEditField('expenses')} />

                                    {/* Payout Information */}
                                    <TechnicianPaySection job={job} onJobUpdated={onJobUpdated} />

                                    {/* Time Log */}
                                    {/* (opened via footer button) */}

                                    {/* Origin Job */}
                                    {job.origin_job && (
                                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                                                <Link className="h-4 w-4 text-indigo-500" />
                                                <h3 className="text-sm font-semibold text-gray-900">Origin Job</h3>
                                            </div>
                                            <div className="divide-y divide-gray-100">
                                                <button
                                                    onClick={() => setViewOriginJobId(job.origin_job!.id)}
                                                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-sm font-medium text-gray-900">#{job.origin_job.invoice_number}</span>
                                                        {job.origin_job.date_executed_start && (
                                                            <span className="text-xs text-gray-500">
                                                                {formatDateOnlyInCompanyTimeZone(job.origin_job.date_executed_start, companyTimeZone)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {job.origin_job.status && (
                                                        <span
                                                            className="text-xs font-medium px-2 py-0.5 rounded-full"
                                                            style={{
                                                                backgroundColor: job.origin_job.status_color || '#e2e8f0',
                                                                color: (() => {
                                                                    const hex = (job.origin_job.status_color || '#e2e8f0').replace('#', '');
                                                                    const r = parseInt(hex.substr(0, 2), 16);
                                                                    const g = parseInt(hex.substr(2, 2), 16);
                                                                    const b = parseInt(hex.substr(4, 2), 16);
                                                                    return ((r * 299 + g * 587 + b * 114) / 1000) > 128 ? '#0f172a' : '#fff';
                                                                })()
                                                            }}
                                                        >
                                                            {job.origin_job.status}
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Follow-up Visits */}
                                    {job.follow_up_jobs && job.follow_up_jobs.length > 0 && (
                                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                                                <CalendarPlus className="h-4 w-4 text-indigo-500" />
                                                <h3 className="text-sm font-semibold text-gray-900">Follow-up Visits</h3>
                                            </div>
                                            <div className="divide-y divide-gray-100">
                                                {job.follow_up_jobs.map((fj) => (
                                                    <button
                                                        key={fj.id}
                                                        onClick={() => setViewFollowUpJobId(fj.id)}
                                                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-sm font-medium text-gray-900">#{fj.invoice_number}</span>
                                                            {fj.date_executed_start && (
                                                                <span className="text-xs text-gray-500">
                                                                    {formatDateOnlyInCompanyTimeZone(fj.date_executed_start, companyTimeZone)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {fj.status && (
                                                            <span
                                                                className="text-xs font-medium px-2 py-0.5 rounded-full"
                                                                style={{
                                                                    backgroundColor: fj.status_color || '#e2e8f0',
                                                                    color: (() => {
                                                                        const hex = (fj.status_color || '#e2e8f0').replace('#', '');
                                                                        const r = parseInt(hex.substr(0, 2), 16);
                                                                        const g = parseInt(hex.substr(2, 2), 16);
                                                                        const b = parseInt(hex.substr(4, 2), 16);
                                                                        return ((r * 299 + g * 587 + b * 114) / 1000) > 128 ? '#0f172a' : '#fff';
                                                                    })()
                                                                }}
                                                            >
                                                                {fj.status}
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Estimates created from this job */}
                                    <LinkedEstimates
                                        filter={{ source_job_id: job.id }}
                                        companyId={job.company?.id}
                                        onOpen={openLinkedEstimate}
                                        variant="desktop"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Map Sidebar and Job Info - Desktop only */}
                        {!isMobile && (
                            <div className="lg:sticky lg:top-6 h-fit space-y-4">
                                <MapWidget
                                    key={`map-${job.id}`}
                                    address={job.address || ''}
                                />
                                {/* Job Info - Collapsible */}
                                <div className={`bg-gray-50 rounded-lg`}>
                                    {/* Collapsible Header */}
                                    <div
                                        className={`flex items-center justify-between ${isMobile ? 'p-3' : 'p-4'} cursor-pointer hover:bg-gray-100 rounded-lg transition-colors`}
                                        onClick={() => setIsJobInfoSidebarExpanded(!isJobInfoSidebarExpanded)}
                                    >
                                        <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 flex items-center gap-2`}>
                                            <FileText className="h-5 w-5 text-blue-600" />
                                            Job Info
                                        </h3>
                                        {isJobInfoSidebarExpanded ? (
                                            <ChevronUp className="h-5 w-5 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="h-5 w-5 text-gray-500" />
                                        )}
                                    </div>

                                    {/* Collapsible Content */}
                                    {isJobInfoSidebarExpanded && (
                                        <div className={`${isMobile ? 'px-3 pb-3' : 'px-4 pb-4'} space-y-3`}>
                                            <div className="flex items-center gap-3">
                                                <Calendar className="h-4 w-4 text-blue-500" />
                                                <div>
                                                    <span className="text-sm font-medium text-gray-700">Date Created:</span>
                                                    <p className="text-sm text-gray-900">{formatInstantInCompanyTimeZone(job.date_created, companyTimeZone)}</p>
                                                </div>
                                            </div>

                                            {job.created_by && (
                                                <div className="flex items-center gap-3">
                                                    <User className="h-4 w-4 text-indigo-500" />
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-700">Created By:</span>
                                                        <p className="text-sm text-gray-900">{job.created_by.name}</p>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-3">
                                                <ArrowRight className="h-4 w-4 text-orange-500" />
                                                <div>
                                                    <span className="text-sm font-medium text-gray-700">Source:</span>
                                                    <div className="-mt-0.5">
                                                        <EditableField
                                                            value={job.source?.id}
                                                            type="select"
                                                            options={[
                                                                { value: '', label: 'No Source' },
                                                                ...(jobSources?.map((source: any) => ({
                                                                    value: source.id,
                                                                    label: source.name
                                                                })) || [])
                                                            ]}
                                                            onSave={(val) => handleFieldSave('source_id', val)}
                                                            disabled={jobSourcesLoading || !canEditField('source')}
                                                            inline={true}
                                                            alwaysShowEdit
                                                            renderValue={(val) => {
                                                                const source = jobSources?.find((s: any) => s.id === val) || job.source;
                                                                return <span className="text-sm text-gray-900">{source?.name || 'No Source'}</span>;
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {job.closed_by && (
                                                <div className="px-4 pb-4 space-y-3">
                                                    <div className="flex justify-between">
                                                        <span className="text-sm font-medium text-gray-700">Closed by:</span>
                                                        <span className="text-gray-900">{job.closed_by.name}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Tags Card */}
                                {job.company?.id && (
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                        <h3 className="text-sm font-medium text-gray-700">Tags</h3>
                                        <TagSelector
                                            companyId={job.company.id}
                                            selectedTags={job.tags || []}
                                            onChange={(tags) => setJobTagsMutation.mutate(tags)}
                                            allowCreate={true}
                                            disabled={!canEditField('tags')}
                                        />
                                    </div>
                                )}

                                {/* Call Masking - admin only, and only on plans that include it */}
                                {isAdmin && isFeatureAllowed('voip.call-masking') && (
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                        <CallMaskingOptions
                                            variant="job"
                                            idPrefix="job_masking"
                                            withEnableAll
                                            route={job.route_calls_to_technician ?? false}
                                            hide={job.hide_contact_phone ?? false}
                                            onChange={(field, value) => handleFieldSave(
                                                field === 'route' ? 'route_calls_to_technician' : 'hide_contact_phone', value)}
                                            disabled={isCompletedJobLocked}
                                        />
                                    </div>
                                )}
                            </div>

                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className={`flex flex-wrap justify-end gap-3 ${paddingClass} border-t border-gray-200 bg-white shrink-0 sticky bottom-0 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] keyboard-aware-footer`}>
                <button
                    onClick={() => setShowImagesModal(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2 relative"
                    title={attachmentCount > 0 ? `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}` : undefined}
                >
                    <span className="relative inline-flex shrink-0">
                        <Camera className="h-4 w-4" />
                        {attachmentCount > 0 && (
                            <span
                                className="absolute -top-2 -right-2 min-h-[1.125rem] min-w-[1.125rem] px-1 flex items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white leading-none shadow-sm ring-2 ring-white"
                                aria-hidden
                            >
                                {attachmentCount > 99 ? '99+' : attachmentCount}
                            </span>
                        )}
                    </span>
                    Photos & Files
                </button>
                <button
                    onClick={() => setShowExpenseAttachmentsModal(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2 relative"
                    title={expenseAttachmentCount > 0 ? `${expenseAttachmentCount} receipt${expenseAttachmentCount === 1 ? '' : 's'}` : undefined}
                >
                    <span className="relative inline-flex shrink-0">
                        <Paperclip className="h-4 w-4" />
                        {expenseAttachmentCount > 0 && (
                            <span
                                className="absolute -top-2 -right-2 min-h-[1.125rem] min-w-[1.125rem] px-1 flex items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white leading-none shadow-sm ring-2 ring-white"
                                aria-hidden
                            >
                                {expenseAttachmentCount > 99 ? '99+' : expenseAttachmentCount}
                            </span>
                        )}
                    </span>
                    Receipts
                </button>
                <button
                    onClick={() => setShowVisitsModal(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2 relative"
                    title="Time Log"
                >
                    <span className="relative inline-flex shrink-0">
                        <Clock className="h-4 w-4" />
                        {job.active_visit && (
                            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                        )}
                    </span>
                    Time Log
                </button>
                <button
                    onClick={() => {
                        // If we came from InvoiceDetailsModal, just close this modal (go back) instead of opening another
                        if (openedFromInvoice) {
                            onClose();
                            return;
                        }
                        setShowInvoiceModal(true);
                    }}
                    disabled={!job?.invoice_number}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center gap-2"
                    title={!job?.invoice_number ? 'Set an invoice number for this job first' : undefined}
                >
                    <Receipt className="h-4 w-4" />
                    Invoice
                </button>
                <TakePaymentButton
                    job={job}
                    disabled={
                        job.payment_status === 'PaidFully' ||
                        (job.total > 0 && (job.payments?.reduce((s, p) => s + p.amount, 0) ?? 0) >= job.total) ||
                        !job.total
                    }
                    onPaymentComplete={(updatedJob) => {
                        if (updatedJob) {
                            // Use the updated job from the PATCH response directly
                            if (onJobUpdated) onJobUpdated(updatedJob);
                        } else {
                            // Stripe path: no updatedJob returned, so refetch
                            queryClient.invalidateQueries({ queryKey: ['jobs'] });
                            if (onJobUpdated) {
                                apiService.getJobById(job.id).then(onJobUpdated);
                            }
                        }
                    }}
                />
                {/* Create Estimate button */}
                <button
                    onClick={() => setShowConvertConfirm(true)}
                    className="px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                    title="Create Estimate"
                >
                    <Repeat className="h-4 w-4" />
                    Create Estimate
                </button>
                {/* Schedule Another Visit button */}
                <button
                    onClick={() => setIsScheduleVisitOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                    title="Schedule Another Visit"
                >
                    <CalendarPlus className="h-4 w-4" />
                    Schedule New Visit
                </button>
                {jobContacts.length > 0 && (
                    <button
                        onClick={() => setContactInfoId(jobContacts[0].id)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                    >
                        <User className="h-4 w-4" />
                        Contact Info
                    </button>
                )}
                {smsContacts.length > 0 && (
                    <div className="relative flex" ref={smsMenuRef}>
                        <button
                            onClick={() => {
                                setSmsContactId(null);
                                setShowSmsMenu((open) => !open);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                        >
                            <MessageSquare className="h-4 w-4" />
                            SMS
                        </button>
                        {showSmsMenu && (
                            <div
                                className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-1"
                                style={smsMenuLayer.style}
                            >
                                {smsContact ? (
                                    <SmsTemplateMenu
                                        job={job}
                                        customerName={smsContact.name || ''}
                                        onBack={smsContacts.length > 1 ? () => setSmsContactId(null) : undefined}
                                        onPickCustom={() => {
                                            setShowSmsMenu(false);
                                            openClientConversation(smsContact);
                                        }}
                                        onPickTemplate={(message, warning) => {
                                            setShowSmsMenu(false);
                                            setTemplateSmsState({
                                                message,
                                                warning,
                                                recipientPhone: smsContact.phone ?? '',
                                                recipientName: smsContact.name || 'Client',
                                                toContactRef: smsContact.phone_masked
                                                    ? `contact:${smsContact.id}:primary`
                                                    : undefined,
                                            });
                                        }}
                                    />
                                ) : (
                                    smsContacts.map((contact) => (
                                        <button
                                            key={contact.id}
                                            type="button"
                                            onClick={() => setSmsContactId(contact.id)}
                                            className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            <User className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                            <span className="flex-1 min-w-0 truncate">{contact.name || 'Client'}</span>
                                            <span className="text-xs text-gray-400">{contact.phone}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
                {isAdmin && (
                    <button
                        onClick={() => setShowCallsModal(true)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                    >
                        <Phone className="h-4 w-4" />
                        Calls
                    </button>
                )}
                {onCancelJob && !job.canceled_at && (
                    <button
                        onClick={() => setShowConfirmCancel(true)}
                        className="px-4 py-2 text-sm font-medium bg-red-600 text-white border border-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                        title="Cancel Job"
                    >
                        Cancel Job
                    </button>
                )}
                {onUncancelJob && job.canceled_at && (
                    <button
                        onClick={() => setShowConfirmUncancel(true)}
                        className="px-4 py-2 text-sm font-medium bg-teal-50 text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                        title="Un-cancel Job"
                    >
                        Un-cancel Job
                    </button>
                )}
            </div>


            {/* Confirm Delete Dialog */}
            {
                onDelete && (
                    <ConfirmDialog
                        isOpen={showConfirmDelete}
                        onClose={() => setShowConfirmDelete(false)}
                        onConfirm={async () => {
                            await onDelete!(job);
                        }}
                        title="Delete Job"
                        message={
                            <div className="space-y-2">
                                <span>Are you sure you want to delete this job?</span>
                                <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
                                <span className="block text-sm text-amber-800">{destructiveCopy.jobDeleteCascade}</span>
                            </div>
                        }
                        confirmText="Delete"
                        cancelText="Cancel"
                    />
                )
            }

            {/* Confirm Cancel Dialog */}
            {
                onCancelJob && (
                    <ConfirmDialog
                        isOpen={showConfirmCancel}
                        onClose={() => {
                            setShowConfirmCancel(false);
                            setCancelReason('');
                        }}
                        onConfirm={async () => {
                            await onCancelJob!(job, cancelReason || undefined);
                        }}
                        title="Cancel Job"
                        message={
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Reason for cancelation (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        placeholder="Enter cancellation reason..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>
                            </div>
                        }
                        confirmText="Cancel Job"
                        cancelText="Go Back"
                    />
                )
            }

            {/* Confirm Un-cancel Dialog */}
            {
                onUncancelJob && (
                    <ConfirmDialog
                        isOpen={showConfirmUncancel}
                        onClose={() => setShowConfirmUncancel(false)}
                        onConfirm={async () => {
                            await onUncancelJob!(job);
                        }}
                        title="Un-cancel Job"
                        message="Are you sure you want to un-cancel this job?"
                        confirmText="Un-cancel Job"
                        cancelText="Go Back"
                    />
                )
            }

            {showConvertConfirm && (
                <CreateEstimateFromJobDialog
                    job={job}
                    onClose={() => setShowConvertConfirm(false)}
                    onDone={handleEstimateCreated}
                />
            )}

            {/* Item Edit Signature Warning Dialog */}
            <ConfirmDialog
                isOpen={showConfirmMarkAsap}
                onClose={() => setShowConfirmMarkAsap(false)}
                onConfirm={async () => {
                    // Same as creating an ASAP job: the date becomes a placeholder for "now".
                    const now = new Date().toISOString();
                    setScheduleExpanded(false);
                    await updateJobMutation.mutateAsync({ is_asap: true, date_executed_start: now, date_executed_end: now });
                }}
                title="Mark job ASAP"
                message="The scheduled time will be removed and the job will be marked ASAP. Set a time again whenever one is agreed."
                confirmText="Mark ASAP"
                cancelText="Go Back"
                confirmButtonClassName="bg-red-600 hover:bg-red-700 text-white"
            />

            <ConfirmDialog
                isOpen={showItemEditSignatureWarning}
                onClose={handleCancelItemEditSignatureWarning}
                onConfirm={handleConfirmItemEditSignatureWarning}
                title="Editing May Void Signature"
                message={"This job's invoice has been signed by the customer.\nEditing items may change the total, which will void their signature.\n\nAre you sure you want to continue?\n\nThe old signed invoice will be kept as a PDF attachment for reference."}
                confirmText="Continue"
                cancelText="Cancel"
                confirmButtonClassName="bg-red-600 hover:bg-red-700 text-white"
            />

            {/* Invoice Details Modal */}
            <InvoiceDetailsModal
                isOpen={showInvoiceModal}
                onClose={() => setShowInvoiceModal(false)}
                jobId={job.id}
                companyId={job.company?.id}
                onInvoiceUpdated={() => {
                    queryClient.invalidateQueries({ queryKey: ['invoice', job.id] });
                    queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    if (onJobUpdated) {
                        apiService.getJobById(job.id).then(onJobUpdated);
                    }
                }}
                openedFromJob={true}
            />
            <JobAttachmentsModal
                isOpen={showImagesModal}
                onClose={() => setShowImagesModal(false)}
                jobId={job.id}
                onAttachmentsUpdated={handleAttachmentsUpdated}
            />
            <JobExpenseAttachmentsModal
                isOpen={showExpenseAttachmentsModal}
                onClose={() => setShowExpenseAttachmentsModal(false)}
                jobId={job.id}
                onAttachmentsUpdated={handleExpenseAttachmentsUpdated}
            />

            {/* Job Visits / Time Log Modal */}
            <JobVisitsModal
                isOpen={showVisitsModal}
                onClose={() => setShowVisitsModal(false)}
                job={job}
                onJobUpdated={onJobUpdated}
            />

            {/* Job Contact Conversation Modal */}
            {/* Templated SMS to the customer (locked 10DLC body) */}
            {templateSmsState && (
                <SendSmsModal
                    isOpen
                    onClose={() => setTemplateSmsState(null)}
                    recipientPhone={templateSmsState.recipientPhone}
                    recipientName={templateSmsState.recipientName}
                    recipientType="Client"
                    jobId={job.id}
                    toContactRef={templateSmsState.toContactRef}
                    initialMessage={templateSmsState.message}
                    warning={templateSmsState.warning}
                    lockMessage
                />
            )}

            <JobContactConversationModal
                isOpen={conversationModalState.isOpen}
                onClose={() => setConversationModalState({ ...conversationModalState, isOpen: false })}
                jobId={conversationModalState.jobId}
                contactId={conversationModalState.contactId}
                which={conversationModalState.which}
                masked={conversationModalState.masked}
                contactName={conversationModalState.contactName}
            />

            {/* Call — dials straight away from a job contact's call button, only asking which number to
                call from when the company has several usable ones */}
            <CallLauncher request={callRequest} onDone={() => setCallRequest(null)} />

            {/* Past Job Modal */}
            {pastJobId && (
                <JobDetailsModal
                    isOpen={!!pastJobId}
                    onClose={() => setPastJobId(null)}
                    jobId={pastJobId}
                    onJobUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    }}
                    onCancelJob={onCancelJob}
                    openedFromPastJobs={true}
                />
            )}

            {/* Schedule Another Visit Modal */}
            <NewJobModal
                isOpen={isScheduleVisitOpen}
                onClose={() => setIsScheduleVisitOpen(false)}
                onJobCreated={() => {
                    setIsScheduleVisitOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['job', job.id] });
                    queryClient.invalidateQueries({ queryKey: ['jobs'] });
                }}
                initialValues={{
                    address: job.address,
                    unit_name: job.unit_name,
                    company_id: job.company?.id,
                    assigned_technician_id: job.assigned_technician?.id,
                    job_type_id: job.job_type?.id,
                    notes: `Additional visit to job #${job.invoice_number}`,
                    origin_job_id: job.id,
                    contacts: (job.contacts ?? []).map(c => c.contact as ContactDTO),
                }}
            />

            {/* Origin Job Modal */}
            {viewOriginJobId && (
                <JobDetailsModal
                    isOpen={!!viewOriginJobId}
                    onClose={() => setViewOriginJobId(null)}
                    jobId={viewOriginJobId}
                    onJobUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    }}
                    onCancelJob={onCancelJob}
                    openedFromPastJobs={true}
                />
            )}

            {/* Follow-up Visit Modal */}
            {viewFollowUpJobId && (
                <JobDetailsModal
                    isOpen={!!viewFollowUpJobId}
                    onClose={() => setViewFollowUpJobId(null)}
                    jobId={viewFollowUpJobId}
                    onJobUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    }}
                    onCancelJob={onCancelJob}
                    openedFromPastJobs={true}
                />
            )}

            {/* Calls for this job */}
            {showCallsModal && (
                <div className="fixed inset-0 flex items-center justify-center p-4" style={callsLayer.style}>
                    <div className="absolute inset-0 bg-black/50" onClick={callsLayer.onClose} />
                    <div className="relative bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-4xl h-[85vh] overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Phone className="h-5 w-5" /> Calls
                            </h2>
                            <button onClick={callsLayer.onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="flex-1 min-h-0">
                            <Suspense fallback={null}>
                                <VoipCallHistoryPage jobId={job.id} />
                            </Suspense>
                        </div>
                    </div>
                </div>
            )}

            {contactInfoId && (
                <JobContactInfoModal
                    jobId={job.id}
                    contacts={jobContacts}
                    contactId={contactInfoId}
                    onSelectContact={setContactInfoId}
                    onClose={() => setContactInfoId(null)}
                    onBeforeNavigate={() => (onDismiss ?? onClose)()}
                />
            )}
        </div >
    );
}
